// src/components/module/compras/DetalleCompraModal.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useContext, useEffect, useState } from "react";
import { toast } from '../../../utils/toast';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Paper,
  Box,
  Divider,
  IconButton,
  Stack,
  alpha,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save,
  CancelOutlined,
  Receipt,
} from "@mui/icons-material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { ICompra } from "../../types/compras.types";
import { useForm, Controller } from "react-hook-form";
import Swal from "sweetalert2";
import detalleCompraApi from "../../../services/api/detalleCompraApi";
import { DarkModeContext } from "../../../context/darkModeContext";

const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C",
    successBg: "#DBEDDB", warning: "#D9730D", warningBg: "#FADEC9",
    error: "#E03E3E", errorBg: "#FBE4E4", hover: "rgba(55,53,47,0.04)",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A",
    successBg: "#1A2F2A", warning: "#C77D48", warningBg: "#2E2215",
    error: "#D44C47", errorBg: "#3D2020", hover: "rgba(255,255,255,0.04)",
  },
};

interface DetalleCompraModalProps {
  open: boolean;
  onClose: () => void;
  compra: ICompra;
  tiposPago: Array<{ Id: number; Nombre: string }>;
  onFinalize: (data: any) => Promise<void>;
  onCancel: () => Promise<void>;
}

interface FormInputs {
  IdTipoPago: number;
  FechaRecibo: Date | null;
  NumeroFactura: string;
  Abono: number;
  Observacion: string;
}

const FieldLabel: React.FC<{ label: string; required?: boolean; color: string }> = ({ label, required, color }) => (
  <Typography sx={{
    fontSize: "0.72rem", fontWeight: 600, color, mb: 0.5,
    textTransform: "uppercase", letterSpacing: "0.04em",
  }}>
    {label}{required && <Box component="span" sx={{ color: "#E03E3E", ml: 0.3 }}>*</Box>}
  </Typography>
);

const DetalleCompraModal: React.FC<DetalleCompraModalProps> = ({
  open, onClose, compra, tiposPago, onFinalize, onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detalleCompra, setDetalleCompra] = useState<any>(null);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const { control, handleSubmit, watch, setError, clearErrors, formState: { errors } } = useForm<FormInputs>({
    defaultValues: {
      IdTipoPago: tiposPago.length > 0 ? tiposPago[0].Id : 0,
      FechaRecibo: new Date(),
      NumeroFactura: "",
      Abono: 0,
      Observacion: "",
    },
  });

  const abono = watch("Abono");

  useEffect(() => {
    if (!compra?.Id) return;
    detalleCompraApi.getByCompraId(compra.Id).then(setDetalleCompra).catch(console.error);
  }, [compra]);

  useEffect(() => {
    const total = detalleCompra?.Total ?? 0;
    const abonoNum = Number(abono) || 0;
    if (abonoNum > 0 && abonoNum !== total) {
      setError("Abono", { type: "manual", message: "El abono debe ser igual al total" });
    } else {
      clearErrors("Abono");
    }
  }, [abono, detalleCompra?.Total]);

  const onSubmit = async (data: FormInputs) => {
    if (!data.FechaRecibo || !data.NumeroFactura?.trim()) return;
    if (Number(data.Abono) !== (detalleCompra?.Total ?? 0)) return;

    setIsSubmitting(true);
    try {
      await onFinalize({
        ...data, IdCompra: compra.Id,
        FechaRecibo: data.FechaRecibo.toISOString().split("T")[0],
      });
      await Swal.fire({ icon: "success", title: "¡Compra finalizada!", timer: 1500, showConfirmButton: false });
      onClose();
    } catch (error) {
      toast.error("Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    const result = await Swal.fire({
      title: "¿Cancelar compra?",
      text: "Esta acción no puede revertirse.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: p.error,
      cancelButtonColor: p.accent,
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No",
    });
    if (!result.isConfirmed) return;
    setIsSubmitting(true);
    try {
      await onCancel();
      await Swal.fire({ icon: "success", title: "Compra cancelada", timer: 1500, showConfirmButton: false });
      onClose();
    } catch (error) {
      toast.error("Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const total = detalleCompra?.Total || 0;
  const saldo = total - Number(abono || 0);
  const money = (v: number) => `$${v.toLocaleString("es-CO", { minimumFractionDigits: 0 })}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{
        sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" },
      }}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center",
          borderBottom: `1px solid ${p.border}`,
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px", bgcolor: p.accentBg,
              display: "flex", alignItems: "center", justifyContent: "center", color: p.accent,
            }}>
              <Receipt sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: p.text }}>
                Gestionar Compra #{compra?.OrdenCompra}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: p.textT }}>
                {compra?.proveedor?.Nombre} · Total: {money(total)}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ width: 32, height: 32, borderRadius: "8px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
            <Grid container spacing={2.5}>
              {/* Tipo de pago */}
              <Grid item xs={12} md={6}>
                <FieldLabel label="Tipo de Pago" required color={p.textT} />
                <FormControl fullWidth size="small" error={!!errors.IdTipoPago}>
                  <Controller name="IdTipoPago" control={control} rules={{ required: "Requerido" }}
                    render={({ field }) => (
                      <Select {...field} displayEmpty sx={{ bgcolor: p.bg }}>
                        {tiposPago.map((t) => <MenuItem key={t.Id} value={t.Id}>{t.Nombre}</MenuItem>)}
                      </Select>
                    )} />
                  <FormHelperText>{errors.IdTipoPago?.message}</FormHelperText>
                </FormControl>
              </Grid>

              {/* Fecha recibo */}
              <Grid item xs={12} md={6}>
                <FieldLabel label="Fecha Recibo" required color={p.textT} />
                <Controller name="FechaRecibo" control={control}
                  render={({ field }) => (
                    <DesktopDatePicker value={field.value} onChange={field.onChange}
                      slotProps={{ textField: { size: "small", fullWidth: true, error: !!errors.FechaRecibo, sx: { "& .MuiInputBase-root": { bgcolor: p.bg } } } }} />
                  )} />
              </Grid>

              {/* Número factura */}
              <Grid item xs={12} md={6}>
                <FieldLabel label="Número de Factura" required color={p.textT} />
                <Controller name="NumeroFactura" control={control} rules={{ required: "Requerido" }}
                  render={({ field }) => (
                    <TextField {...field} size="small" fullWidth error={!!errors.NumeroFactura}
                      helperText={errors.NumeroFactura?.message} placeholder="Ej: FAC-001234"
                      sx={{ "& .MuiInputBase-root": { bgcolor: p.bg } }} />
                  )} />
              </Grid>

              {/* Abono */}
              <Grid item xs={12} md={6}>
                <FieldLabel label="Abono" required color={p.textT} />
                <Controller name="Abono" control={control}
                  render={({ field }) => (
                    <TextField {...field} type="number" size="small" fullWidth
                      error={!!errors.Abono} helperText={errors.Abono?.message}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      sx={{ "& .MuiInputBase-root": { bgcolor: p.bg } }} />
                  )} />
              </Grid>

              {/* Observaciones */}
              <Grid item xs={12}>
                <FieldLabel label="Observaciones" color={p.textT} />
                <Controller name="Observacion" control={control}
                  render={({ field }) => (
                    <TextField {...field} multiline rows={2} fullWidth size="small" placeholder="Notas sobre esta compra..."
                      sx={{ "& .MuiInputBase-root": { bgcolor: p.bg } }} />
                  )} />
              </Grid>

              {/* Resumen de pago */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{
                  p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg,
                }}>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 650, color: p.text, mb: 2 }}>
                    Resumen de Pago
                  </Typography>
                  <Stack spacing={1.2}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: "0.84rem", color: p.textS }}>Total compra</Typography>
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {money(total)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: "0.84rem", color: p.textS }}>Abono</Typography>
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: p.success, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {money(Number(abono || 0))}
                      </Typography>
                    </Stack>
                    <Divider sx={{ borderColor: p.border }} />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: p.text }}>Saldo pendiente</Typography>
                      <Typography sx={{
                        fontSize: "1.1rem", fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
                        color: saldo > 0 ? p.error : p.success,
                      }}>
                        {money(Math.max(0, saldo))}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{
            px: 3, py: 2, borderTop: `1px solid ${p.border}`, bgcolor: p.bg,
            justifyContent: "space-between",
          }}>
            <Button onClick={handleCancel} disabled={isSubmitting}
              startIcon={<CancelOutlined sx={{ fontSize: 16 }} />}
              sx={{
                color: p.error, fontSize: "0.82rem", fontWeight: 600,
                borderRadius: "8px", px: 2, "&:hover": { bgcolor: p.errorBg },
              }}>
              Cancelar Compra
            </Button>
            <Stack direction="row" spacing={1.5}>
              <Button onClick={onClose} sx={{
                color: p.textS, fontSize: "0.82rem", fontWeight: 600,
                borderRadius: "8px", "&:hover": { bgcolor: p.hover },
              }}>
                Cerrar
              </Button>
              <Button type="submit" variant="contained" disabled={isSubmitting} disableElevation
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: p.accent, color: "#FFF", px: 3, fontSize: "0.82rem", fontWeight: 600,
                  borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" },
                }}>
                {isSubmitting ? "Procesando..." : "Finalizar Compra"}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
  );
};

export default DetalleCompraModal;
